import React, { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import Dropdown from "antd/es/dropdown";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import CustomField from "@mapmycustomers/shared/types/customField/CustomField";
import { EntityTypesSupportingFieldCustomization } from "@mapmycustomers/shared/types/entity";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";
import { Button } from "@mapmycustomers/ui";

import MoreButton from "@app/component/MoreButton";
import Table from "@app/component/Table";
import { getCustomFieldsForEntity } from "@app/store/customFields";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { formatDate } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";
import { getFieldVisibilityName } from "@app/util/ui";

import { getActiveEntityType, getFields, isArchiveMode, isLoading } from "../../store";
import { getArchiveActionMenuItem } from "../ArchiveAction";
import ArchiveAlert from "../ArchiveAlert";
import { getCloneActionMenuItem } from "../CloneAction";
import ConfirmFieldDeletionModal from "../ConfirmFieldDeletionModal";
import DeleteAction from "../DeleteAction";
import EditAction from "../EditAction";
import FieldType from "../FieldType";
import UnarchiveAction from "../UnarchiveAction";

import styles from "./FieldsTable.module.scss";

interface Props {
  analyticIssuer?: AnalyticsService;
  customFields: CustomField[];
  entityType: EntityTypesSupportingFieldCustomization;
  fields: SchemaField[];
  isArchiveMode: boolean;
  loading: boolean;
  onEditSchemaField?: (field: SchemaField) => void;
  onSelectSchemaFields?: (selectedSchemaFields: SchemaField[]) => void;
  selectedSchemaFieldsKeys?: SchemaField["id"][];
}

const FieldsTable: React.FC<Props> = ({
  analyticIssuer,
  customFields,
  entityType,
  fields,
  isArchiveMode,
  loading,
  onEditSchemaField,
  onSelectSchemaFields,
  selectedSchemaFieldsKeys,
}) => {
  const intl = useIntl();
  const [customFieldIdToDelete, setCustomFieldIdToDelete] = useState<
    CustomField["id"] | undefined
  >();

  const fieldModel = getFieldModelByEntityType(entityType);

  const expressionFields = useMemo(
    () => new Set(customFields.map(({ expressionFields }) => expressionFields ?? []).flat()),
    [customFields]
  );

  const customFieldsMap = useMemo(() => {
    const map = new Map();
    customFields.forEach((cf) => {
      map.set(cf.id, cf);
    });
    return map;
  }, [customFields]);

  const handleEditClick = useCallback(
    (field: SchemaField) => onEditSchemaField?.(field),
    [onEditSchemaField]
  );

  const handleDelete = useCallback(
    (field: SchemaField) => {
      setCustomFieldIdToDelete(field.customFieldId ? field.customFieldId : undefined);
    },
    [setCustomFieldIdToDelete]
  );

  const handlePageChange = useCallback(() => {
    onSelectSchemaFields?.([]);
  }, [onSelectSchemaFields]);

  const rowSelection = useMemo(
    () => ({
      onChange: (selectedRowKeys: React.Key[], selectedSchemaFields: SchemaField[]) => {
        onSelectSchemaFields?.(selectedSchemaFields);
      },
      selectedRowKeys: selectedSchemaFieldsKeys,
    }),
    [onSelectSchemaFields, selectedSchemaFieldsKeys]
  );

  return (
    <>
      <Table<SchemaField>
        className={styles.table}
        columns={[
          {
            dataIndex: "field",
            key: "field",
            render: (fieldName: string) => {
              const field = fieldModel.getByPlatformName(fieldName);
              return (
                <span className={styles.fieldName}>
                  {field?.fieldManagementDisplayName ?? fieldName}
                </span>
              );
            },
            title: intl.formatMessage({
              id: "settings.manageFields.grid.header.fieldName",
              defaultMessage: "Field Name",
              description: "Header of Field Name column",
            }),
            width: "25%",
          },
          {
            dataIndex: "id",
            key: "fieldOperation",
            render: (_, schemaField: SchemaField) => {
              return (
                <div className={styles.operation}>
                  {isArchiveMode ? (
                    <UnarchiveAction field={schemaField} />
                  ) : (
                    <EditAction field={schemaField} onAction={handleEditClick} />
                  )}
                </div>
              );
            },
          },
          {
            dataIndex: "fieldType",
            key: "fieldType",
            render: (_, schemaField: SchemaField) => (
              <FieldType
                customField={customFieldsMap.get(schemaField.customFieldId)}
                field={schemaField}
              />
            ),
            title: intl.formatMessage({
              id: "settings.manageFields.grid.header.fieldType",
              defaultMessage: "Type",
              description: "Header of Type column",
            }),
            width: "20%",
          },
          {
            dataIndex: "visibility",
            key: "visibility",
            render: (_, schemaField: SchemaField) => {
              return isArchiveMode ? (
                <span>{getFieldVisibilityName(intl, schemaField.visibility)}</span>
              ) : (
                <Button
                  className={styles.visibilityBtn}
                  onClick={() => handleEditClick(schemaField)}
                  type="link"
                >
                  {getFieldVisibilityName(intl, schemaField.visibility)}
                </Button>
              );
            },
            title: intl.formatMessage({
              id: "settings.manageFields.grid.header.fieldAccess",
              defaultMessage: "Field Access",
              description: "Header of Field Access column",
            }),
            width: "20%",
          },
          {
            dataIndex: "updatedAt",
            key: "updatedAt",
            render: (updatedAt: string) => {
              const value = formatDate(parseApiDate(updatedAt), "PPpp");
              return (
                <div>
                  <Tooltip placement="top" title={value}>
                    <div className={styles.date}>{value}</div>
                  </Tooltip>
                </div>
              );
            },
            title: isArchiveMode
              ? intl.formatMessage({
                  id: "settings.manageFields.grid.header.archivedOn",
                  defaultMessage: "Archived on",
                  description: "Header of Archived on column",
                })
              : intl.formatMessage({
                  id: "settings.manageFields.grid.header.updatedAt",
                  defaultMessage: "Updated",
                  description: "Header of Updated column",
                }),
            width: "15%",
          },
          {
            dataIndex: "id",
            key: "actions",
            render: (_, schemaField: SchemaField) => {
              return isArchiveMode ? (
                <DeleteAction
                  expressionFields={expressionFields}
                  field={schemaField}
                  onAction={handleDelete}
                />
              ) : (
                <Dropdown
                  menu={{
                    items: [
                      getArchiveActionMenuItem(schemaField),
                      getCloneActionMenuItem(schemaField),
                    ],
                  }}
                  trigger={["click"]}
                >
                  <MoreButton horizontal />
                </Dropdown>
              );
            },
            title: intl.formatMessage({
              id: "settings.manageFields.grid.header.actions",
              defaultMessage: "Actions",
              description: "Header of Actions column",
            }),
            width: "10%",
          },
        ]}
        components={{
          body: {
            wrapper: ({ children, ...props }: PropsWithChildren<{}>) => (
              <tbody {...props}>
                <ArchiveAlert />
                {children}
              </tbody>
            ),
          },
        }}
        dataSource={fields}
        loading={loading}
        pagination={{
          onChange: handlePageChange,
          pageSize: 20,
          position: ["bottomCenter"],
          showSizeChanger: false,
        }}
        rowKey="id"
        rowSelection={rowSelection}
        scrollable
        showBorder
      />

      {customFieldIdToDelete && (
        <ConfirmFieldDeletionModal
          analyticIssuer={analyticIssuer}
          customFieldId={customFieldIdToDelete}
          entityType={entityType}
          onHide={() => setCustomFieldIdToDelete(undefined)}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  customFields: getCustomFieldsForEntity(state)(getActiveEntityType(state)),
  entityType: getActiveEntityType(state),
  fields: getFields(state),
  isArchiveMode: isArchiveMode(state),
  loading: isLoading(state),
});

export default connect(mapStateToProps)(FieldsTable);
